// Forum posts + replies API. Replaces the Firestore ``posts`` collection.

import { apiClient } from './apiClient';

// Canonical Plumescript-team author identity. Mirrors the constant
// in ``seed_forum_starter.py``. Posts authored by this email are
// displayed as "Admin" regardless of the stored ``author_name`` —
// this covers prod rows that haven't been reseeded yet, and any
// future team writes that bypass the seeder.
const TEAM_AUTHOR_EMAIL = 'admin@plumescript.app';
const TEAM_AUTHOR_DISPLAY = 'Admin';

// Normalise how a forum row's author renders in the UI. Takes a
// post or reply dict (anything with ``author_email`` /
// ``author_name`` / ``author_username``); returns the display
// string. Order of preference for non-team rows: username → name →
// email local-part → empty.
export function forumAuthorDisplay(row) {
  if (!row) return '';
  const email = (row.author_email || '').toLowerCase();
  if (email === TEAM_AUTHOR_EMAIL) return TEAM_AUTHOR_DISPLAY;
  return (
    row.author_username ||
    row.author_name ||
    row.author_email ||
    ''
  );
}

// ``lang`` selects the locale subset: ``fr|en`` filters, ``all``
// (or omitted) returns every locale. Backend also returns
// ``counts: {fr, en, all}`` on the response for the chip-strip UI;
// when the caller passes ``includeCounts=true`` we return the full
// envelope instead of just ``posts``.
export async function listPosts({ tag, limit = 50, lang, includeCounts = false } = {}) {
  const params = { tag: tag ?? '', limit };
  if (lang) params.lang = lang;
  const { data } = await apiClient.get('/api/forum/posts/', { params });
  if (includeCounts) return { posts: data?.posts ?? [], counts: data?.counts ?? null };
  return data?.posts ?? [];
}

export async function createPost({ title, body, tags = [], authorName = '', language }) {
  const payload = {
    title,
    body,
    tags: Array.isArray(tags) ? tags.join(',') : tags,
    author_name: authorName,
  };
  if (language) payload.language = language;
  const { data } = await apiClient.post('/api/forum/posts/', payload, {
    headers: { 'Content-Type': 'application/json' },
  });
  return data;
}

export async function getPost(postId) {
  const { data } = await apiClient.get(`/api/forum/posts/${postId}/`);
  return data;
}

export async function updatePost(postId, patch) {
  const body = { ...patch };
  if (Array.isArray(body.tags)) body.tags = body.tags.join(',');
  const { data } = await apiClient.patch(`/api/forum/posts/${postId}/`, body, {
    headers: { 'Content-Type': 'application/json' },
  });
  return data;
}

export async function deletePost(postId) {
  await apiClient.delete(`/api/forum/posts/${postId}/`);
}

export async function createReply(postId, { body, authorName = '', attachmentUrl = '' }) {
  const { data } = await apiClient.post(
    `/api/forum/posts/${postId}/replies/`,
    {
      body,
      author_name: authorName,
      // Optional inline image attachment URL (public S3 permalink)
      // written via the existing uploadFile presign pipeline. Empty
      // string when no attachment; BE persists as
      // ``ForumReply.attachment_url`` and echoes it back in the
      // reply payload.
      attachment_url: attachmentUrl,
    },
    { headers: { 'Content-Type': 'application/json' } },
  );
  return data;
}

export async function deleteReply(replyId) {
  await apiClient.delete(`/api/forum/replies/${replyId}/`);
}
